import { useEffect, useState } from 'react';

const SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'equity', label: 'Equity & Returns' },
  { id: 'monthly', label: 'Monthly Results' },
  { id: 'trades', label: 'Recent Trades' },
  { id: 'account', label: 'Account' },
];

/**
 * A slim, sticky in-page navigation for the (long) performance report.
 *
 * Because the page stacks several data-heavy blocks, this bar lets visitors
 * jump straight to a section. It sits right below the fixed site header and
 * highlights whichever section is currently under the header as you scroll.
 * The links are plain `#` anchors, so they work without any client router and
 * rely on the CSS `scroll-margin` set on each target section.
 */
export default function SectionNav() {
  const [active, setActive] = useState(SECTIONS[0].id);

  useEffect(() => {
    const ids = SECTIONS.map((section) => section.id);

    const update = () => {
      /* A section becomes "current" once its top passes just under the header. */
      const threshold = 170;
      let current = ids[0];
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= threshold) {
          current = id;
        }
      });
      setActive(current);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div className="sticky top-[66px] md:top-[74px] z-40 border-b border-background-200/80 bg-background-50/95 backdrop-blur-md">
      <div className="w-full px-4 md:px-8">
        <nav
          className="max-w-shell mx-auto flex items-center gap-1 overflow-x-auto no-scrollbar py-2"
          aria-label="On this page"
        >
          <span className="hidden lg:flex items-center gap-2 pr-2 text-xs font-semibold uppercase tracking-[0.16em] text-foreground-400 whitespace-nowrap">
            <i className="ri-map-pin-2-line" aria-hidden="true"></i>
            On this page
          </span>
          {SECTIONS.map((section) => {
            const isActive = active === section.id;
            return (
              <a
                key={section.id}
                href={`#${section.id}`}
                aria-current={isActive ? 'true' : undefined}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                }`}
              >
                {section.label}
              </a>
            );
          })}
        </nav>
      </div>
    </div>
  );
}